import { useEffect, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  IconButton,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import { useSnackbar } from "notistack";

import CeroInput from "../../../components/CeroInput";
import CeroAutoComplete from "../../../components/CeroAutoComplete";
import CeroSideSheetDrawer from "../../../components/CeroSideSheetDrawer";
import { months } from "../../../constants";
import { createReportValidation } from "./schema";
import {
  resetReportStatus,
  createReport,
  listAssessmentCycleCountry,
} from "../../../redux/actions";
import { STATUS } from "../../../redux/constants";

import useStyles from "./styles";
import CeroChipSelect from "../../../components/CeroChipSelect";
import CeroSelect from "../../../components/CeroSelect";
import { ExpandMore, ExpandLess, Key } from "@mui/icons-material";
import CeroInfoPair from "../../../components/CeroInfoPair";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

const CreateReportDrawer = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedValues, setSelectedValues] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const { reportData, isOpen } = props;
  const topicList = useSelector((state) => state.listings.topicList.data);
  const frameworkList = useSelector(
    (state) => state.listings.frameworkList.data
  );
  const topicFetchStatus = useSelector(
    (state) => state.listings.topicList.status
  );
  const createReportStatus = useSelector(
    (state) => state.reports.createReport.status
  );
  const createReportError = useSelector(
    (state) => state.reports.createReport.message
  );
  const countryData = useSelector(
    (state) => state.listings.getOrganizationCountry.data.country_data
  );
  const countryFacilityData = useSelector(
    (state) => state.listings.getOrganizationCountry.data.country_facility_data
  );
  const assessmentYearData = useSelector(
    (state) => state.listings.getAssessmentYear.data
  );
  const countryFacilityDataObject = {};

  const esgoptions = [
    {
      id: "esg",
      label: "ESG",
    },
    {
      id: "environmental",
      label: "Environmental",
      category: "ESG",
    },
    {
      id: "social",
      label: "Social",
      category: "ESG",
    },
    {
      id: "governance",
      label: "Governance",
      category: "ESG",
    },
    {
      id: "carbon_offset",
      label: "Carbon Offset",
    },
    {
      id: "assessments",
      label: "Assessments",
    },
    {
      id: "materiality",
      label: "Materiality",
      category: "Assessments",
    },
    {
      id: "supplier_assessment",
      label: "Supplier Assessment",
      category: "Assessments",
    },
    {
      id: "sustainability_risk_assessment",
      label: "Sustainability Risk Assessment",
      category: "Assessments",
    },
  ];

  for (const key in countryFacilityData) {
    Object.assign(countryFacilityDataObject, countryFacilityData[key]);
  }
  const isButtonLoading = createReportStatus === STATUS.RUNNING;

  const topicOptionList = topicList.map((item) => ({
    id: item.id,
    label: item.name,
  }));

  const countryList = Object.entries(countryData).map(([key, value]) => ({
    key: value,
    value: key,
  }));
  const frameworkOptionList = frameworkList.map((item) => ({
    key: item.id,
    value: item.name,
  }));

  const yearList = assessmentYearData?.map((item) => ({
    id: item,
    label: item,
  }));

  const assessmentList = useSelector(
    (state) => state.assessment.listAssessmentCycleCountry.data
  );

  const createReportForm = useFormik({
    initialValues: {
      name: reportData ? reportData.name : "",
      year: "",
      framework: reportData ? reportData.framework_id : "",
      topic: esgoptions || "",
      start_month: reportData ? reportData.start_month : "",
      end_month: reportData ? reportData.end_month : "",
      country_code: Array.isArray(reportData?.countries)
        ? reportData?.countries?.[0] || ""
        : reportData?.countries || "",
      country: Array.isArray(reportData?.countries)
        ? reportData?.countries?.[0] || ""
        : reportData?.countries || "",
      period: reportData?.period,
    },
    validationSchema: createReportValidation,
    enableReinitialize: true,
    onSubmit: () => {},
  });

  const filteredValues = selectedValues.filter(
    (item) => item !== "esg" && item !== "assessments"
  );

  const onSubmitReportData = () => {
    const payload = {
      framework_id: createReportForm.values.framework,
      name: createReportForm.values.name,
      year: createReportForm.values.year,
      topic_id: filteredValues,
      start_month:
        createReportForm.values.start_month ||
        monthsRef.indexOf(
          createReportForm.values.period?.split("-")[0].trim()
        ) + 1,
      end_month:
        createReportForm.values.end_month ||
        monthsRef.indexOf(
          createReportForm.values.period?.split("-")[1].trim()
        ) + 1,
      country_code: createReportForm.values.country_code,
      period: createReportForm.values.period,
    };

    if (filterCountryFacility.length > 0) {
      var apiFacilitites = [];
      for (const data of filterCountryFacility) {
        apiFacilitites.push(countryFacilityDataObject[data]);
      }
      payload.facilities = apiFacilitites;
    }
    dispatch(createReport(payload));
  };
  const onClose = useCallback(() => {
    createReportForm.resetForm();
    props.onClose();
  }, [createReportForm, props]);

  useEffect(() => {
    if (createReportStatus === STATUS.SUCCESS) {
      enqueueSnackbar("Report created successfully", { variant: "success" });
      dispatch(resetReportStatus());
      setSelectedValues([]);
      setCountry([]);
      setCountryFacility([]);
      onClose();
    } else if (createReportStatus === STATUS.ERROR) {
      enqueueSnackbar(
        createReportError.message ||
          "We couldn't process your request. Please try again later.",
        { variant: "error" }
      );
      dispatch(resetReportStatus());
      setSelectedValues([]);
      setCountry([]);
      setCountryFacility([]);
    }
  }, [
    createReportStatus,
    enqueueSnackbar,
    onClose,
    dispatch,
    createReportError.message,
  ]);

  useEffect(() => {
    if (topicFetchStatus === STATUS.SUCCESS) {
      createReportForm.setFieldValue("topic_id", topicOptionList);
    }
  }, [topicFetchStatus]);

  const handleCheckboxChange = (event) => {
    const value = event.target.value;
    const isChecked = event.target.checked;

    if (value === "all") {
      if (selectedValues.length === esgoptions.length) {
        setSelectedValues([]);
        setSelectAll(false);
      } else {
        setSelectedValues(esgoptions.map((item) => item.id));
        setSelectAll(true);
      }
    } else {
      let updatedValues = [...selectedValues];
      const parentItem = Object.values(groupedOptions).find(
        ({ parent }) => parent?.id === value
      );
      const childItems = parentItem
        ? parentItem.children.map((child) => child.id)
        : [];
      if (parentItem) {
        if (isChecked) {
          updatedValues = [...updatedValues, value, ...childItems];
        } else {
          updatedValues = updatedValues.filter(
            (id) => id !== value && !childItems.includes(id)
          );
        }
      } else {
        const parent = Object.values(groupedOptions).find(({ children }) =>
          children.some((child) => child.id === value)
        );
        if (isChecked) {
          updatedValues.push(value);
          if (
            parent &&
            parent.children.every((child) => updatedValues.includes(child.id))
          ) {
            updatedValues.push(parent.parent.id);
          }
        } else {
          updatedValues = updatedValues.filter((id) => id !== value);
          if (parent) {
            updatedValues = updatedValues.filter(
              (id) => id !== parent.parent.id
            );
          }
        }
      }
      setSelectedValues([...new Set(updatedValues)]);
      setSelectAll(updatedValues.length === esgoptions.length);
    }
  };

  const [filterCountry, setCountry] = useState([]);
  const [facilitites, setFacility] = useState(countryFacilityDataObject);
  const [filterCountryFacility, setCountryFacility] = useState([]);
  const [frameWork, setFrameWork] = useState("");
  const [frameWorkPeriods, setFrameWorkPeriods] = useState([]);
  const [frameWorkActlPrd, setFrameWorkActlPrd] = useState("");
  const getFacility = (countries) => {
    const countryFacilityDataObject = {};
    var availableFacility = [];
    for (const item of countries) {
      Object.assign(countryFacilityDataObject, countryFacilityData[item]);
      availableFacility = availableFacility.concat(
        Object.keys(countryFacilityData[item])
      );
    }
    const persistedfacilitySelection = availableFacility.filter((element) =>
      filterCountryFacility.includes(element)
    );
    setFacility(countryFacilityDataObject);
    setCountryFacility(persistedfacilitySelection);
  };

  const countryHandleChange = (event) => {
    const value = event.target.value;

    dispatch(listAssessmentCycleCountry({ country: value }));
    setCountry(value);
    getFacility([value]);

    // Map selected country names to their codes
    // const selectedCountryCodes = value.map((country) => countryData[country]);
    createReportForm.setFieldValue("country_code", value);
    createReportForm.setFieldValue("country", value);
  };

  useEffect(() => {
    const filteredList =
      assessmentList?.find(
        (item) =>
          item.assessment_year === createReportForm.values.year &&
          item.country === createReportForm.values.country_code
      ) || [];
    setFrameWork(filteredList?.framework);
    let frmk = frameworkList.find(
      (item) => item.name === filteredList?.framework
    );
    setFrameWorkActlPrd(
      `${monthsRef[filteredList?.assessment_start_month - 1]} ${
        filteredList?.assessment_end_month <
        filteredList?.assessment_start_month
          ? filteredList?.assessment_year - 1
          : filteredList?.assessment_year
      } 
      -${monthsRef[filteredList?.assessment_end_month - 1]} ${
        filteredList?.assessment_year
      }`
    );
    createReportForm.setFieldValue("framework", frmk?.id);
    let periods = generatePeriods(
      filteredList?.assessment_start_month,
      filteredList?.assessment_end_month,
      filteredList?.audit_cycle
    );

    let catItem = periods.map((item) => {
      return {
        key: `${item[0].slice(0, 3)}-${item.at(-1).slice(0, 3)}`,
        value: `${item[0].slice(0, 3)}-${item.at(-1).slice(0, 3)}`,
      };
    });
    if (catItem.length) catItem.push({ key: "Custom", value: "Custom" });
    setFrameWorkPeriods(catItem);
  }, [createReportForm.values.year, createReportForm.values.country_code]);

  const FacilityHandleChange = (event) => {
    const value = event.target.value;
    if (value[value.length - 1] === "all") {
      setCountryFacility(
        filterCountryFacility.length === Object.keys(facilitites).length
          ? []
          : Object.keys(facilitites)
      );
      return;
    }
    setCountryFacility(value);
  };

  const [expanded, setExpanded] = useState({});

  const groupedOptions = esgoptions.reduce((acc, topic) => {
    if (!topic.category) {
      acc[topic.id] = { parent: topic, children: [] };
    } else {
      const parentKey = esgoptions.find(
        (item) => item.label === topic.category
      )?.id;
      if (parentKey) {
        acc[parentKey] = acc[parentKey] || { parent: null, children: [] };
        acc[parentKey].children.push(topic);
      }
    }
    return acc;
  }, {});

  const getPrimaryPaymentDrawer = () => {
    return (
      <Box className={classes.mainContainer}>
        <CeroInput
          required
          id="name"
          name="name"
          label="Report Name"
          fullWidth
          value={createReportForm.values.name}
          onChange={createReportForm.handleChange}
          onBlur={createReportForm.handleBlur}
          error={createReportForm.touched.year && createReportForm.errors.name}
        />
        {/* <CeroChipSelect
          label={"Country"}
          formControlClass={classes.MultiSelectformControl}
          optionsde={countryData}
          selected={filterCountry}
          handleChange={countryHandleChange}
        /> */}
        <CeroSelect
          classes={{ container: classes.selectContainer }}
          id="country"
          name="country"
          label="Country"
          fullWidth
          options={countryList}
          selectedValue={createReportForm.values.country}
          onChange={countryHandleChange}
          onBlur={createReportForm.handleBlur}
          error={
            createReportForm.touched.country && createReportForm.errors.country
          }
        />
        <CeroChipSelect
          label={"Facility"}
          formControlClass={classes.MultiSelectformControl}
          optionsde={
            Object.keys(facilitites).length > 0
              ? facilitites
              : countryFacilityDataObject
          }
          selected={filterCountryFacility}
          handleChange={FacilityHandleChange}
        />
        <Grid>
          <Grid className={classes.gridContainer}>
            <Grid style={{ width: "48%", margin: "8px 0" }}>
              <CeroAutoComplete
                className={classes.yearFilter}
                required
                id="year"
                name="year"
                label="Assessment Year"
                fullWidth
                options={yearList}
                value={createReportForm.values?.year}
                onChange={(e, value) => {
                  createReportForm.setFieldValue("year", value?.id);
                }}
                onBlur={createReportForm.handleBlur}
                error={
                  createReportForm.touched.year && createReportForm.errors.year
                }
                isOptionEqualToValue={(option, value) =>
                  option.id === value?.id
                }
              />
            </Grid>
            {frameWork && (
              <Grid className={classes.gridItem}>
                <CeroSelect
                  classes={{ container: classes.selectContainer }}
                  id="period"
                  name="period"
                  label="Period"
                  fullWidth
                  options={frameWorkPeriods}
                  selectedValue={createReportForm.values.period || ""}
                  onChange={createReportForm.handleChange}
                  onBlur={createReportForm.handleBlur}
                  error={
                    createReportForm.touched.period &&
                    createReportForm.errors.period
                  }
                />
              </Grid>
            )}
          </Grid>
          <Grid className={classes.gridContainer}>
            {frameWork && (
              <Grid
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  margin: "5px 0px 15px 0px",
                }}
              >
                {/* <CeroSelect
                classes={{ container: classes.selectContainer }}
                id="framework"
                name="framework"
                label="Framework"
                fullWidth
                options={frameworkOptionList}
                selectedValue={createReportForm.values.framework || ""}
                onChange={createReportForm.handleChange}
                onBlur={createReportForm.handleBlur}
                error={
                  createReportForm.touched.framework &&
                  createReportForm.errors.framework
                }
              /> */}
                <Box className={classes.frameNameBox}>
                  <div className={classes.frameDiv}>
                    <InsertDriveFileIcon className={classes.frameworkIcon} />
                    <Typography style={{ fontWeight: 200 }}>
                      Framework
                    </Typography>
                  </div>
                  <Typography className={classes.frameNameTypo}>
                    {frameWork}
                  </Typography>
                </Box>
                <Box className={classes.frameNameBox}>
                  <div className={classes.frameDiv}>
                    <CalendarMonthIcon className={classes.frameworkIcon} />
                    <Typography style={{ fontWeight: 200 }}>Period</Typography>
                  </div>
                  <Typography className={classes.frameNameTypo}>
                    {frameWorkActlPrd}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>

          {createReportForm.values.period === "Custom" && (
            <Grid className={classes.gridContainer}>
              <Grid className={classes.gridItem}>
                <CeroSelect
                  classes={{ container: classes.selectContainer }}
                  id="start_month"
                  name="start_month"
                  label="Start Month"
                  fullWidth
                  options={months}
                  selectedValue={createReportForm.values.start_month || ""}
                  onChange={createReportForm.handleChange}
                  onBlur={createReportForm.handleBlur}
                  error={
                    createReportForm.touched.start_month &&
                    createReportForm.errors.start_month
                  }
                />
              </Grid>

              <Grid className={classes.gridItem}>
                <CeroSelect
                  classes={{ container: classes.selectContainer }}
                  id="end_month"
                  name="end_month"
                  label="End Month"
                  fullWidth
                  options={months}
                  selectedValue={createReportForm.values.end_month || ""}
                  onChange={createReportForm.handleChange}
                  onBlur={createReportForm.handleBlur}
                  error={
                    createReportForm.touched.end_month &&
                    createReportForm.errors.end_month
                  }
                />
              </Grid>
            </Grid>
          )}
        </Grid>
        <InputLabel
          className={
            createReportForm.errors.end_month
              ? classes["input-padding"]
              : classes["no-padding"]
          }
          id="demo-multiple-name-label"
        >
          Select Topics
        </InputLabel>
        <Box className={classes.checkbox}>
          <FormGroup>
            <FormControlLabel
              key={"all"}
              control={
                <Checkbox
                  className={classes.selectCheckbox}
                  checked={selectAll}
                  onChange={handleCheckboxChange}
                  value={"all"}
                  style={{
                    color: "#8C2148",
                  }}
                />
              }
              label={"Select All"}
            />
            <Box>
              {Object.values(groupedOptions).map(({ parent, children }) => (
                <Box key={parent?.id} mb={2} sx={{ position: "relative" }}>
                  {parent && (
                    <Box display="flex" alignItems="center">
                      <FormControlLabel
                        control={
                          <Checkbox
                            className={classes.selectCheckbox}
                            // checked={selectedValues.includes(parent.id)}
                            checked={
                              children.length === 0
                                ? selectedValues.includes(parent.id)
                                : children.every((child) =>
                                    selectedValues.includes(child.id)
                                  )
                            }
                            onClick={() => {
                              if (!expanded[parent.id]) {
                                setExpanded((prev) => ({
                                  ...prev,
                                  [parent.id]: true,
                                }));
                              }
                            }}
                            onChange={(e) => {
                              handleCheckboxChange(e);
                              if (e.target.checked && !expanded[parent.id]) {
                                setExpanded((prev) => ({
                                  ...prev,
                                  [parent.id]: true,
                                }));
                              }
                            }}
                            value={parent.id}
                            style={{
                              color: "#8C2148",
                            }}
                          />
                        }
                        label={parent.label}
                      />
                      {children.length > 0 && (
                        <IconButton
                          size="small"
                          onClick={() =>
                            setExpanded((prev) => ({
                              ...prev,
                              [parent.id]: !prev[parent.id],
                            }))
                          }
                        >
                          {expanded[parent.id] ? (
                            <ExpandLess />
                          ) : (
                            <ExpandMore />
                          )}
                        </IconButton>
                      )}
                    </Box>
                  )}

                  {children.length > 0 && expanded[parent?.id] && (
                    <Box
                      sx={{
                        position: "absolute",
                        left: "42px",
                        top: "45px",
                        bottom: "23px",
                        width: "1px",
                        backgroundColor: "#8C2148",
                      }}
                    />
                  )}

                  {expanded[parent?.id] &&
                    children.map((child, index) => (
                      <Box
                        key={child.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          ml: 15.5,
                          position: "relative",
                        }}
                      >
                        <Box
                          sx={{
                            position: "absolute",
                            left: "-20px",
                            top: "50%",
                            width: "20px",
                            height: "1px",
                            backgroundColor: "#8C2148",
                          }}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              className={classes.selectCheckbox}
                              checked={selectedValues.includes(child.id)}
                              onChange={handleCheckboxChange}
                              value={child.id}
                              style={{
                                color: "#8C2148",
                              }}
                            />
                          }
                          label={child.label}
                        />
                      </Box>
                    ))}
                </Box>
              ))}
            </Box>
          </FormGroup>
        </Box>
      </Box>
    );
  };
  const monthsRef = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const generatePeriods = (startMonth, endMonth, periodType) => {
    const allMonths = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    let monthsInYear = [];
    if (startMonth > endMonth) {
      monthsInYear = allMonths
        .slice(startMonth - 1)
        .concat(allMonths.slice(0, endMonth));
    } else {
      monthsInYear = allMonths.slice(startMonth - 1, endMonth);
    }

    const periods = [];
    if (periodType === "quarterly") {
      const monthsPerPeriod = Math.ceil(monthsInYear.length / 4);
      for (let i = 0; i < 4; i++) {
        const start = i * monthsPerPeriod;
        const end = start + monthsPerPeriod;
        periods.push(monthsInYear.slice(start, end));
      }
    } else if (periodType === "half_yearly") {
      const monthsPerPeriod = Math.ceil(monthsInYear.length / 2);
      for (let i = 0; i < 2; i++) {
        const start = i * monthsPerPeriod;
        const end = start + monthsPerPeriod;
        periods.push(monthsInYear.slice(start, end));
      }
    } else if (periodType === "yearly") {
      periods.push(monthsInYear.slice(0, 12));
    }

    return periods;
  };

  return (
    <CeroSideSheetDrawer
      primaryDrawer={{
        drawerOpen: isOpen,
        onClose: onClose,
        content: getPrimaryPaymentDrawer(),
        header: { title: "Create report" },
        footer: {
          primaryBtnTitle: isButtonLoading ? "Saving..." : "Save",
          secondaryBtnTitle: "Cancel",
          primaryBtnAction: onSubmitReportData,
          secondaryBtnAction: onClose,
          disablePrimaryBtn: !(
            createReportForm.values.name !== "" &&
            ((createReportForm.values.start_month &&
              createReportForm.values.end_month) ||
              (createReportForm.values.period &&
                createReportForm.values.period !== "Custom"))
          ),
        },
        classes: {
          drawerContainer: classes.drawerContainer,
          contentArea: classes.drawerContentArea,
        },
      }}
    />
  );
};

export default CreateReportDrawer;
